import { useEffect, useState } from "react";
import { Button, Card, Cluster, Grid, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface CapacityItem {
  name: string;
  kind: "volume" | "bucket";
  detail?: string;
  total_gib?: number;
  used_gib: number | null;
  free_gib?: number;
  free_percent: number | null;
  low: boolean;
  error: string | null;
}

interface Report {
  checked_at: string;
  alert_free_percent: number;
  alert_recipient: string;
  items: CapacityItem[];
  low: string[];
}

export function CapacityPage() {
  const [data, setData] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      setData(await api.get<Report>("/operator/capacity"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load AWS capacity.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <Stack gap={4}>
      <Cluster gap={3}>
        <TipHeading
          as="h2"
          label="AWS capacity"
          tip="Disk and database space on AWS, and what is stored in S3. Support is emailed when a volume has 10% or less free."
        />
        <Button variant="secondary" onClick={() => void load()}>
          Refresh
        </Button>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}
      {data && data.low.length > 0 && (
        <Flash tone="error">
          Low space: {data.low.join(", ")}. {data.alert_recipient} is emailed (at most once a day per volume).
        </Flash>
      )}
      <Grid columns={3}>
        {data?.items.map((item) => (
          <Card key={item.name}>
            <Stack gap={2}>
              <strong>{item.name}</strong>
              {item.error ? (
                <span>Unavailable: {item.error}</span>
              ) : item.kind === "volume" ? (
                <>
                  <span>{item.free_percent}% free{item.low ? " - LOW" : ""}</span>
                  <progress max={100} value={100 - (item.free_percent ?? 0)} aria-label={`${item.name} used`} />
                  <span>
                    {item.used_gib} of {item.total_gib} GiB used ({item.free_gib} GiB free)
                  </span>
                </>
              ) : (
                <span>{item.used_gib} GiB stored</span>
              )}
              {item.detail && <small>{item.detail}</small>}
            </Stack>
          </Card>
        ))}
      </Grid>
      {data && (
        <small>
          Checked {formatUtc(data.checked_at)}. Alert threshold: {data.alert_free_percent}% free, sent to{" "}
          {data.alert_recipient}.
        </small>
      )}
    </Stack>
  );
}
